import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { buildDynamicFilters } from '@/lib/dynamic-filters';
import { SearchValues } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { ApiResponse } from '@/types/api.type';
import api from '@/lib/api';

const parseNumber = (input: string): { value: number; op?: string } | null => {
  if (!input || !input.trim()) return null;

  const trimmed = input.trim().replace(/,/g, '');
  const ops = ['<=', '>=', '!=', '<', '>', '='];
  let op: string | undefined = '=';
  let numStr = trimmed;

  for (const operator of ops) {
    if (trimmed.startsWith(operator)) {
      op = operator === '==' ? '=' : operator;
      numStr = trimmed.substring(operator.length).trim();
      break;
    }
  }

  const num = Number(numStr);
  if (Number.isNaN(num)) return null;
  return { value: num, op };
};

const parseDateRange = (input: string): { op: string; from: string; to: string } | null => {
  if (!input || !input.trim()) return null;

  const match = input.match(/>=(\d{2}\/\d{2}\/\d{4})&<=(\d{2}\/\d{2}\/\d{4})/);
  if (!match) return null;

  const [, fromStr, toStr] = match;
  const parseDate = (dateStr: string): string => {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  return {
    op: 'range',
    from: parseDate(fromStr),
    to: parseDate(toStr)
  };
};

const cleanParams = (params: any): Record<string, any> => {
  if (!params || typeof params !== 'object') return {};

  return Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>
  );
};

const hasSearchFilters = (filters: SearchValues) => Object.keys(filters || {}).length > 0;

interface UseDynamicSearchProps {
  onSearch: (params: any) => Promise<void>;
  params: any;
  onInitialSearch?: (formData: any, onSearchFn: (params: any) => Promise<void>) => Promise<void>;
  getColumns: () => { field: string; type?: string }[];
  endpoint: string;
  pageSize?: number;
  sumField?: string;
}

interface CachedSearchResult {
  rows: any[];
  total: number;
  totalRow: any | null;
}

interface SearchState {
  filters: SearchValues;
  rows: any[];
  total: number;
  page: number;
  loading: boolean;
  totalRow: any | null;
  fetchTrigger: number;
}

const createInitialSearchState = (): SearchState => ({
  filters: {},
  rows: [],
  total: 0,
  page: 0,
  loading: false,
  totalRow: null,
  fetchTrigger: 0
});

export const useDynamicSearch = ({
  onSearch,
  params,
  onInitialSearch,
  getColumns,
  endpoint,
  pageSize = 50,
  sumField
}: UseDynamicSearchProps) => {
  const { entity } = useAuth();
  const normalizedEndpoint = useMemo(() => normalizeEndpoint(endpoint), [endpoint]);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
  const cleanedParams = useMemo(() => cleanParams(params), [paramsKey]);

  const [state, setState] = useState<SearchState>(() => createInitialSearchState());

  const isFirst = useRef(true);
  const abortController = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, CachedSearchResult>>(new Map());
  const lastRequestSignature = useRef<string>('');
  const columnMeta = useRef<{ fields: string[]; numeric: Set<string> }>(
    (() => {
      const cols = getColumns();
      return {
        fields: cols.map(col => col.field),
        numeric: new Set(cols.filter(col => col.type === 'number').map(col => col.field))
      };
    })()
  );

  const buildFilters = useCallback((values: SearchValues) => {
    const filterData: Record<
      string,
      string | number | { value: number; op?: string } | { op: string; from: string; to: string } | undefined
    > = {};

    columnMeta.current.fields.forEach(field => {
      const value = values[field];
      if (value === undefined || value === null) return;

      if (typeof value === 'number') {
        if (!Number.isNaN(value)) {
          filterData[field] = value;
        }
        return;
      }

      if (Array.isArray(value) && value.length > 0) {
        const joinedValue = value.map(v => String(v).replace(/^contains:/, '')).join(';');
        filterData[field] = `contains:${joinedValue}`;
        return;
      }

      if (typeof value === 'string' && value.trim()) {
        const dateRange = parseDateRange(value);
        if (dateRange) {
          filterData[field] = dateRange;
          return;
        }

        if (columnMeta.current.numeric.has(field)) {
          const parsed = parseNumber(value);
          if (parsed) {
            filterData[field] = parsed;
            return;
          }
        }

        filterData[field] = value;
      }
    });

    return buildDynamicFilters(filterData);
  }, []);

  const buildRequestKey = useCallback(
    (page: number, filters: Record<string, any>, cleanFormParams: Record<string, any>) => {
      return JSON.stringify({
        entitySlug: entity?.slug,
        endpoint: normalizedEndpoint,
        page,
        pageSize,
        params: cleanFormParams,
        filters,
        sumField
      });
    },
    [entity?.slug, normalizedEndpoint, pageSize, sumField]
  );

  const prefetchNextPage = useCallback(
    async (page: number, filters: Record<string, any>, cleanFormParams: Record<string, any>, total: number) => {
      if (!entity?.slug) return;

      const totalPages = Math.ceil((total || 0) / pageSize);
      const nextPage = page + 1;
      if (totalPages <= 1 || nextPage >= totalPages) {
        return;
      }

      const requestKey = buildRequestKey(nextPage, filters, cleanFormParams);
      if (cacheRef.current.has(requestKey)) {
        return;
      }

      const body = {
        ...cleanFormParams,
        ...(Object.keys(filters).length > 0 && { dynamic_filters: filters }),
        ...(sumField && { sum: [sumField] })
      };

      try {
        const response = await api.post<ApiResponse<any>>(`/entities/${entity.slug}/erp/${normalizedEndpoint}`, body, {
          params: {
            page: nextPage + 1,
            page_size: pageSize
          }
        });

        const results = response.data.results || response.data || [];
        const totalRow = (response.data as any).totals || (response.data as any).total || null;
        cacheRef.current.set(requestKey, {
          rows: results,
          total: response.data.count || 0,
          totalRow
        });
      } catch {
        return;
      }
    },
    [buildRequestKey, entity?.slug, normalizedEndpoint, pageSize, sumField]
  );

  const filtersKey = useMemo(() => JSON.stringify(state.filters), [state.filters]);
  const hasFilters = hasSearchFilters(state.filters);

  const currentRequest = useMemo(() => {
    const filters = buildFilters(state.filters);
    return {
      filters,
      cleanParams: cleanedParams,
      requestKey: buildRequestKey(state.page, filters, cleanedParams)
    };
  }, [buildFilters, buildRequestKey, cleanedParams, state.filters, state.page]);

  const requestSignature = useMemo(
    () =>
      JSON.stringify({
        entitySlug: entity?.slug,
        endpoint: normalizedEndpoint,
        pageSize,
        sumField,
        paramsKey,
        filtersKey,
        page: state.page,
        fetchTrigger: state.fetchTrigger
      }),
    [entity?.slug, normalizedEndpoint, pageSize, sumField, paramsKey, filtersKey, state.page, state.fetchTrigger]
  );

  const onSearchChange = useCallback((values: SearchValues) => {
    const nextFilters = values || {};

    if (!hasSearchFilters(nextFilters)) {
      abortController.current?.abort();
      lastRequestSignature.current = '';
      setState(prev => {
        if (
          !hasSearchFilters(prev.filters) &&
          prev.rows.length === 0 &&
          prev.total === 0 &&
          prev.page === 0 &&
          !prev.loading
        ) {
          return prev;
        }
        return createInitialSearchState();
      });
      return;
    }

    const nextFiltersKey = JSON.stringify(nextFilters);
    setState(prev => {
      if (JSON.stringify(prev.filters) === nextFiltersKey) {
        return prev;
      }

      return {
        ...prev,
        filters: nextFilters,
        rows: [],
        total: 0,
        page: 0,
        loading: false,
        totalRow: null,
        fetchTrigger: prev.fetchTrigger + 1
      };
    });
  }, []);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      lastRequestSignature.current = requestSignature;
      return;
    }

    if (!hasFilters || !entity?.slug) {
      return;
    }

    if (lastRequestSignature.current === requestSignature) {
      return;
    }

    lastRequestSignature.current = requestSignature;
    abortController.current?.abort();

    const fetchData = async () => {
      const cachedResult = cacheRef.current.get(currentRequest.requestKey);

      if (cachedResult) {
        setState(prev => ({
          ...prev,
          rows: cachedResult.rows,
          total: cachedResult.total,
          loading: false,
          totalRow: cachedResult.totalRow
        }));
        void prefetchNextPage(state.page, currentRequest.filters, currentRequest.cleanParams, cachedResult.total);
        return;
      }

      const controller = new AbortController();
      abortController.current = controller;
      setState(prev => (prev.loading ? prev : { ...prev, loading: true }));

      try {
        const body = {
          ...currentRequest.cleanParams,
          ...(Object.keys(currentRequest.filters).length > 0 && { dynamic_filters: currentRequest.filters }),
          ...(sumField && { sum: [sumField] })
        };

        const response = await api.post<ApiResponse<any>>(`/entities/${entity.slug}/erp/${normalizedEndpoint}`, body, {
          params: {
            page: state.page + 1,
            page_size: pageSize
          },
          signal: controller.signal
        });

        if (controller.signal.aborted) {
          return;
        }

        const results = response.data.results || response.data || [];
        const totalRow = (response.data as any).totals || (response.data as any).total || null;
        const cachedPayload = {
          rows: results,
          total: response.data.count || 0,
          totalRow
        };

        cacheRef.current.set(currentRequest.requestKey, cachedPayload);
        setState(prev => ({
          ...prev,
          rows: results,
          total: response.data.count || 0,
          loading: false,
          totalRow
        }));
        void prefetchNextPage(state.page, currentRequest.filters, currentRequest.cleanParams, response.data.count || 0);
      } catch (error: any) {
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }

        console.error('Error fetching filtered data:', error);
        setState(prev => ({
          ...prev,
          rows: [],
          total: 0,
          loading: false,
          totalRow: null
        }));
      } finally {
        if (abortController.current === controller) {
          abortController.current = null;
        }
      }
    };

    void fetchData();
  }, [
    currentRequest,
    entity?.slug,
    hasFilters,
    normalizedEndpoint,
    pageSize,
    prefetchNextPage,
    requestSignature,
    state.page,
    sumField
  ]);

  useEffect(() => {
    cacheRef.current.clear();
  }, [entity?.slug, normalizedEndpoint, pageSize, paramsKey, sumField]);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  const onPageChange = useCallback((page: number) => {
    setState(prev => {
      if (prev.page === page) {
        return prev;
      }

      return {
        ...prev,
        page
      };
    });
  }, []);

  const handleInitialSearch = useCallback(
    async (formData: any) => {
      const filters = buildFilters(state.filters);
      const cleanFormData = cleanParams(formData);
      const body = {
        ...cleanFormData,
        ...(Object.keys(filters).length > 0 && { dynamic_filters: filters })
      };

      if (onInitialSearch) {
        await onInitialSearch(body, onSearch);
      } else {
        await onSearch(body);
      }
    },
    [state.filters, buildFilters, onInitialSearch, onSearch]
  );

  const handleRefresh = useCallback(async () => {
    if (hasFilters) {
      cacheRef.current.clear();
      abortController.current?.abort();
      setState(prev => ({
        ...prev,
        rows: [],
        total: 0,
        page: 0,
        loading: false,
        totalRow: null,
        fetchTrigger: prev.fetchTrigger + 1
      }));
      return;
    }

    await onSearch(cleanedParams);
  }, [cleanedParams, hasFilters, onSearch]);

  return {
    searchState: {
      filterParams: state.filters,
      dynamicFilterParams: currentRequest.filters,
      searchResults: state.rows,
      totalCount: state.total,
      currentPage: state.page,
      isSearching: state.loading,
      totalRow: state.totalRow
    },
    handleSearchChange: onSearchChange,
    handleSearchPageChange: onPageChange,
    hasFilterParams: hasFilters,
    handleInitialSearch,
    handleRefreshClick: handleRefresh,
    isSearching: state.loading
  };
};
